import { AsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// import { ReactComponent as AdvanceBookMarkIcon } from '../../../assets/icons/advance-bookmark-icon.svg';
// import { ReactComponent as SaveBookMarkIcon } from '../../../assets/icons/save-bookmark-icon.svg';
import { filterType } from '../../../config/enum';
import {
  CommonFilterProperties,
  FilterUpdateProperties,
} from '../../../interface/filter.interface';
import { useAppDispatch } from '../../../redux/hooks';
import MyButton from '../../button/Button';
import CustomCheckbox from '../../custom-check-box/CustomCheckBox';
// import CustomDatePicker from '../../custom-date-range/CustomDatePicker';
import CustomDatePickers from '../../custom-date-range/CustomDatePickers';
import CustomDateRange from '../../custom-date-range/CustomDateRange';
import CustomInput from '../../custom-input/CustomInput';
import CustomTimeRange from '../../custom-time-input/CustomTimeRange';
import MultiSelectDropdown from '../../multi-select-field/MultiSelectField';
import SelectField from '../../select-field/SelectField';

interface CustomTableFilterV2Properties {
  filters: CommonFilterProperties[];
  filterListLoading: boolean;
  filtersToDispatch: (payload: FilterUpdateProperties) => PayloadAction<FilterUpdateProperties>;
  filterListToDispatch?: AsyncThunk<any, void, any>;
  onClose?: () => void; // Add onClose prop
}

/**
 * Function to dynamically create and apply custom filters
 * @param {any} properties Custom table filter properties
 * @returns {any} Table filter v2 component
 */
function CustomTableFilterV2(properties: CustomTableFilterV2Properties) {
  const { t, i18n } = useTranslation('sidePanel');
  const [activeTab] = useState<'advance' | 'bookmark'>('advance');
  const { filters, filterListLoading, filtersToDispatch, filterListToDispatch, onClose } =
    properties;

  const dispatch = useAppDispatch();
  const [selectedFilter, setSelectedFilter] = useState<string>();
  const [pendingFilters, setPendingFilters] = useState<CommonFilterProperties[]>(filters);
  /**
   * Function to update pending filters
   * @param {any} name filter name
   * @param {any} value filter value
   */
  const handleChangeFilters = (name: string, value: string | number) => {
    setPendingFilters((previous) =>
      previous.map((filter) =>
        filter.key === name
          ? { ...filter, value: typeof value === 'number' ? value.toString() : value }
          : filter,
      ),
    );
  };

  /**
   * Function to handle multiple filter selections (for checkboxes)
   * @param {any} name - The filter key
   * @param {any} value - The value being toggled
   */
  const handleChangeMutipleFilters = (name: string, value: string | number) => {
    setPendingFilters((previous) =>
      previous.map((filter) => {
        if (filter.key !== name) return filter;

        // Convert current value to array form
        let currentValues: string[] = [];
        if (Array.isArray(filter.value)) {
          currentValues = [...filter.value];
        } else if (filter.value) {
          currentValues = [filter.value.toString()];
        }

        const valueString = value.toString();

        // Toggle value
        const newValues = currentValues.includes(valueString)
          ? currentValues.filter((v) => v !== valueString)
          : [...currentValues, valueString];

        return {
          ...filter,
          value: newValues,
          valueToShow:
            filter.listOfValue
              ?.filter((item) => newValues.includes(item.value))
              .map((item) => item.label) || [],
        };
      }),
    );
  };

  /**
   * Function to dispatch the updated date filters
   * @param {any} name - The field name (key1 or key2)
   * @param {any} value - The new value
   */
  const handleChangeDateFilters = (name: string, value: string | number) => {
    setPendingFilters((previous) =>
      previous.map((filter) => {
        if (filter.type === filterType.DATE_RANGE || filter.type === filterType.TIME_RANGE) {
          if (filter.key1 === name) {
            return {
              ...filter,
              value1: value.toString(),
              value2: '', // Clear to date when from date changes
            };
          }
          if (filter.key2 === name) {
            return { ...filter, value2: value.toString() };
          }
        }
        return filter;
      }),
    );
  };

  /**
   * Function to dispatch the updated angle bracket filters
   * @param {any} name field name
   * @param {any} value new value
   */
  const handleAngleBracketFilters = (name: string, value: string | number) => {
    setPendingFilters((previousFilters) =>
      previousFilters.map((filter) => {
        if (filter.type === filterType.ANGLE_BRACKETS) {
          // Update either value1 or value2 based on which field changed
          if (filter.key1 === name) {
            return { ...filter, value1: value.toString() };
          }
          if (filter.key2 === name) {
            return { ...filter, value2: value.toString() };
          }
        }
        return filter;
      }),
    );
  };

  /**
   * Function to handle multi select field update
   * @param {any} name filter name
   * @param {any} value array of selected values
   */
  const handleMultiSelectField = (name: string, value: string[]) => {
    setPendingFilters((previous) =>
      previous.map((f) =>
        f.key === name
          ? {
              ...f,
              value,
              valueToShow: f.listOfValue
                ? f.listOfValue
                    .filter((item) => value.includes(item.value))
                    .map((item) => item.label)
                : [],
            }
          : f,
      ),
    );
  };

  /**
   * Clears all applied filters by dispatching a clear action for each filter.
   */
  const handleClearFilters = () => {
    if (onClose) {
      onClose();
    }
  };

  // Check if any pending filter is selected
  const isAnyFilterSelected = pendingFilters.some((filter) => {
    // Check standard filters with 'value' property
    const hasStandardValue =
      (Array.isArray(filter.value) && filter.value.length > 0) ||
      (!Array.isArray(filter.value) && filter.value !== '' && filter.value !== undefined);

    // Check date range filters
    const hasDateRangeValue =
      (filter.type === filterType.DATE_RANGE || filter.type === filterType.TIME_RANGE) &&
      ((filter.value1 && filter.value1 !== '') || (filter.value2 && filter.value2 !== ''));

    // Check angle bracket filters
    const hasAngleBracketValue =
      filter.type === filterType.ANGLE_BRACKETS &&
      ((filter.value1 && filter.value1 !== '') || (filter.value2 && filter.value2 !== ''));

    return hasStandardValue || hasDateRangeValue || hasAngleBracketValue;
  });

  /**
   * Applies the currently selected filters by dispatching an "apply" action for each active filter.
   */
  // const handleApplyFilter = (): void => {
  //   pendingFilters.forEach((filter) => {
  //     if (
  //       (Array.isArray(filter.value) && filter.value.length > 0) ||
  //       (!Array.isArray(filter.value) && filter.value !== '' && filter.value !== undefined)
  //     ) {
  //       dispatch(
  //         filtersToDispatch({
  //           key: filter.key,
  //           value: filter.value,
  //           type: 'add',
  //           valueToShow: filter.valueToShow,
  //         })
  //       );
  //     }
  //   });
  // };
  const handleApplyFilter = (): void => {
    pendingFilters.forEach((filter) => {
      if (filter.type === filterType.MULTISELECT || filter.type === filterType.CHECKBOX) {
        dispatch(
          filtersToDispatch({
            key: filter.key,
            value: '',
            type: 'remove',
          }),
        );
      }
    });
    pendingFilters.forEach((filter) => {
      const isMultiSelect =
        filter.type === filterType.MULTISELECT || filter.key === 'electricityContractType';
      const isCheckbox = filter.type === filterType.CHECKBOX || filter.key === 'avaiable';

      // Handle multi-select filters
      if (isMultiSelect && Array.isArray(filter.value) && filter.value.length > 0) {
        dispatch(
          filtersToDispatch({
            key: filter.key,
            value: filter.value,
            type: 'add',
            valueToShow: filter.valueToShow,
          }),
        );
      }
      // Handle checkbox filters
      else if (isCheckbox && Array.isArray(filter.value) && filter.value.length > 0) {
        filter.value.forEach((singleValue) => {
          dispatch(
            filtersToDispatch({
              key: filter.key,
              value: singleValue.toString(),
              type: 'add',
              valueToShow: [singleValue],
            }),
          );
        });
      }
      // Handle single value filters (including date filters)
      else if (!Array.isArray(filter.value) && filter.value !== '' && filter.value !== undefined) {
        dispatch(
          filtersToDispatch({
            key: filter.key,
            value: filter.value.toString(),
            type: 'add',
            valueToShow: filter.valueToShow,
          }),
        );
      }

      // Special handling for date range filters
      if (filter.type === filterType.DATE_RANGE) {
        // Dispatch start date if exists
        if (filter.key1 && filter.value1) {
          dispatch(
            filtersToDispatch({
              key: filterType.DATE_RANGE,
              value: filter.value1.toString(),
              type: 'add',
              subKey: filter.key1, // or any identifier you want
            }),
          );
        }
        // Dispatch end date if exists
        if (filter.key2 && filter.value2) {
          dispatch(
            filtersToDispatch({
              key: filterType.DATE_RANGE,
              value: filter.value2.toString().toString(),
              type: 'add',
              subKey: filter.key2, // or any identifier you want
            }),
          );
        }
      }
      if (filter.type === filterType.ANGLE_BRACKETS) {
        // Dispatch the input value if exists
        if (filter.key1 && filter.value1) {
          dispatch(
            filtersToDispatch({
              key: filterType.ANGLE_BRACKETS,
              value: filter.value1.toString(),
              type: 'add',
              subKey: filter.key1,
            }),
          );
        }
        // Dispatch the operator if exists
        if (filter.key2 && filter.value2) {
          dispatch(
            filtersToDispatch({
              key: filterType.ANGLE_BRACKETS,
              value: filter.value2.toString(),
              type: 'add',
              subKey: filter.key2,
            }),
          );
        }
      }
    });
    handleClearFilters();
  };

  useEffect(() => {
    setPendingFilters(filters);
  }, [filters]);

  useEffect(() => {
    const withoutDefaultFilters = filters.filter((filter) => !filter.searchBy);
    if (!selectedFilter && withoutDefaultFilters.length) {
      setSelectedFilter(withoutDefaultFilters[0].key);
    }
  }, [filters, selectedFilter]);

  useEffect(() => {
    const withoutDefaultFilters = filters.filter(
      (filter) => !filter.searchBy && !filter.FEAddedFilter,
    );
    if (withoutDefaultFilters.length === 0 && filterListToDispatch) {
      dispatch(filterListToDispatch());
    }
  }, [dispatch, filterListToDispatch, filters]);

  return (
    <div className="custom-table-filter-wrap-v2" dir={i18n.dir()}>
      {/* <div className="custom-table-tabs">
        <div
          className={`tab ${activeTab === 'advance' ? 'active' : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => setActiveTab('advance')}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              setActiveTab('advance');
            }
          }}>
          {activeTab === 'advance' && (
            <span className="tab-icons">
              <AdvanceBookMarkIcon />
            </span>
          )}
          {t('advanceSearch')}
        </div>
        <div
          className={`tab ${activeTab === 'bookmark' ? 'active' : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => setActiveTab('bookmark')}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              setActiveTab('bookmark');
            }
          }}>
          {activeTab === 'bookmark' && (
            <span className="tab-icon">
              <SaveBookMarkIcon />
            </span>
          )}
          {t('savedBookmark')}
        </div>
        <div className="border-tabs"> </div>
      </div> */}
      <div>
        {activeTab === 'advance' &&
          (filterListLoading ? (
            <div className="loader-container">
              <div className="lds-dual-ring" />
            </div>
          ) : (
            <div className="custom-table-filter-container">
              <div className="available-filter-headers">
                {filters
                  .filter((filter) => !filter.searchBy)
                  .map((filter) => (
                    <div
                      className={
                        filter.key === selectedFilter ? 'filter-header active' : 'filter-header'
                      }
                      role="presentation"
                      onClick={() => setSelectedFilter(filter.key)}>
                      <span
                        className={
                          filter.key === selectedFilter ? 'active-filter' : 'in-active-filter'
                        }
                      />
                      <h6
                        className={
                          filter.applyFilter || filter.key === selectedFilter ? 'applied' : ''
                        }>
                        {t(filter.name)}{' '}
                      </h6>
                      {(filter.type === filterType.CHECKBOX ||
                        filter.type === filterType.MULTISELECT ||
                        filter.type === filterType.SELECT_FIELD ||
                        filter.type === filterType.TIME_RANGE ||
                        filter.type === filterType.ANGLE_BRACKETS) &&
                        filter.listOfValue?.length !== undefined && (
                          <span className="available-filter-length">
                            {' '}
                            {filter.listOfValue.length}
                          </span>
                        )}
                    </div>
                  ))}
              </div>
              {/* <div className="hr-line" /> */}
              <div className="selected-filter-value">
                <div className="selected-filter-border"> </div>
                {(() => {
                  const filterToDisplay = pendingFilters.find(
                    (filter) => filter.key === selectedFilter,
                  );

                  if (!filterToDisplay) return null;

                  let valueToShow;
                  if (filterToDisplay.type === filterType.CHECKBOX) {
                    if (filterToDisplay.key === 'deviceStatus') {
                      const connectivityValues = filterToDisplay.listOfValue?.filter(
                        (item) => item.value === 'offline' || item.value === 'online',
                      ) || [];
                      const availabilityValues = filterToDisplay.listOfValue?.filter(
                        (item) => item.value === 'available' || item.value === 'unavailable',
                      ) || [];

                      valueToShow = (
                        <div className="device-status-grouped-wrap">
                          <div className="status-group">
                            <p className="status-group-title">
                              {t('connectivityStatus', 'Connectivity Status')}
                            </p>
                            <div className="checkbox-input-wrap">
                              {connectivityValues.map((singleValue) => (
                                <CustomCheckbox
                                  key={singleValue.value}
                                  checkboxLabel={
                                    t(singleValue.label).length > 20
                                      ? `${t(singleValue.label).substring(0, 20)}...`
                                      : t(singleValue.label)
                                  }
                                  name={filterToDisplay.key}
                                  value={
                                    Array.isArray(filterToDisplay.value) &&
                                    filterToDisplay.value.includes(singleValue.value)
                                  }
                                  onChangeValue={() =>
                                    handleChangeMutipleFilters(
                                      filterToDisplay.key,
                                      singleValue.value,
                                    )
                                  }
                                  fontSize="small"
                                />
                              ))}
                            </div>
                          </div>
                          <div className="status-group" style={{ marginTop: '12px' }}>
                            <p className="status-group-title">
                              {t('availabilityStatus', 'Availability Status')}
                            </p>
                            <div className="checkbox-input-wrap">
                              {availabilityValues.map((singleValue) => (
                                <CustomCheckbox
                                  key={singleValue.value}
                                  checkboxLabel={
                                    t(singleValue.label).length > 20
                                      ? `${t(singleValue.label).substring(0, 20)}...`
                                      : t(singleValue.label)
                                  }
                                  name={filterToDisplay.key}
                                  value={
                                    Array.isArray(filterToDisplay.value) &&
                                    filterToDisplay.value.includes(singleValue.value)
                                  }
                                  onChangeValue={() =>
                                    handleChangeMutipleFilters(
                                      filterToDisplay.key,
                                      singleValue.value,
                                    )
                                  }
                                  fontSize="small"
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    } else {
                      valueToShow = (
                        <div className="checkbox-input-wrap">
                          {filterToDisplay.listOfValue?.map((singleValue) => (
                            <CustomCheckbox
                              key={singleValue.value}
                              checkboxLabel={
                                t(singleValue.label).length > 20
                                  ? `${t(singleValue.label).substring(0, 20)}...`
                                  : t(singleValue.label)
                              }
                              name={filterToDisplay.key}
                              value={
                                Array.isArray(filterToDisplay.value) &&
                                filterToDisplay.value.includes(singleValue.value)
                              }
                              onChangeValue={() =>
                                handleChangeMutipleFilters(filterToDisplay.key, singleValue.value)
                              }
                              fontSize="small"
                            />
                          ))}
                        </div>
                      );
                    }
                  } else if (filterToDisplay.type === filterType.DATE) {
                    valueToShow = (
                      <div className="checkbox-input-wrap">
                        <CustomDatePickers
                          name={filterToDisplay.key}
                          value={filterToDisplay.value.toString()}
                          placeholder={t(filterToDisplay.name)}
                          readOnly={false}
                          onChangeValue={handleChangeFilters}
                        />
                      </div>
                    );
                  } else if (filterToDisplay.type === filterType.DATE_RANGE) {
                    valueToShow = (
                      <div className="date-range-input-wrap">
                        <CustomDateRange
                          onChangeValue={handleChangeDateFilters}
                          inputLabel={t(filterToDisplay.name)}
                          value1={filterToDisplay?.value1 || ''}
                          value2={filterToDisplay?.value2 || ''}
                          name1={filterToDisplay?.key1 || 'startDate'}
                          name2={filterToDisplay?.key2 || 'endDate'}
                          isFutureDateDisabled
                        />
                      </div>
                    );
                  } else if (filterToDisplay.type === filterType.TIME_RANGE) {
                    valueToShow = (
                      <div className="time-range-input-wrap">
                        <CustomTimeRange
                          onChangeValue={handleChangeDateFilters}
                          inputLabel={t(filterToDisplay.name)}
                          value1={filterToDisplay?.value1 || ''}
                          value2={filterToDisplay?.value2 || ''}
                          name1={filterToDisplay?.key1 || 'startTime'}
                          name2={filterToDisplay?.key2 || 'endTime'}
                        />
                      </div>
                    );
                  } else if (filterToDisplay.type === filterType.MULTISELECT) {
                    valueToShow = (
                      <div className="multi-select-field-wrap">
                        <MultiSelectDropdown
                          name={filterToDisplay.key}
                          placeholderFilled={t(filterToDisplay.name)}
                          title={t(filterToDisplay.name)}
                          options={
                            filterToDisplay?.listOfValue?.map((item) => ({
                              ...item,
                              label: t(item.label),
                            })) || []
                          }
                          placeholder={t(filterToDisplay.name)}
                          search
                          version="v2"
                          selectedValues={
                            Array.isArray(filterToDisplay.value) ? filterToDisplay.value : []
                          }
                          onSelectionChange={handleMultiSelectField}
                          fontSize="small"
                        />
                      </div>
                    );
                  } else if (filterToDisplay.type === filterType.ANGLE_BRACKETS) {
                    valueToShow = (
                      <div className="angle-brackets-wrap">
                        <CustomInput
                          name={filterToDisplay.key1 || ''}
                          inputLabel={t(filterToDisplay.name)}
                          type="text"
                          placeholder={t('enterValue')}
                          value={filterToDisplay.value1 || ''}
                          onChangeValue={handleAngleBracketFilters}
                          fontSize="small"
                        />
                        <SelectField
                          name={filterToDisplay.key2 || ''}
                          inputLabel={t('selectOperator')}
                          selectedValue={filterToDisplay.value2 || ''}
                          placeholder={t('selectOperator')}
                          onChangeValue={handleAngleBracketFilters}
                          options={
                            filterToDisplay.listOfValue?.map((item) => ({
                              ...item,
                              label: t(item.label),
                            })) || []
                          }
                          fontSize="small"
                        />
                      </div>
                    );
                  } else if (filterToDisplay.type === filterType.SELECT_FIELD) {
                    valueToShow = (
                      <div className="select-field-wrap">
                        <SelectField
                          name={filterToDisplay.key}
                          options={
                            filterToDisplay?.listOfValue?.map((item) => ({
                              ...item,
                              label: t(item.label),
                            })) || []
                          }
                          placeholder={t(filterToDisplay.name)}
                          inputLabel={t(filterToDisplay.name)}
                          selectedValue={filterToDisplay.value.toString() || ''}
                          onChangeValue={handleChangeFilters}
                          fontSize="small"
                          enableSearch={filterToDisplay.key !== 'status'}
                        />
                      </div>
                    );
                  }
                  return valueToShow;
                })()}
              </div>
            </div>
          ))}

        {activeTab === 'bookmark' && (
          <div className="selected-filte-bookmark">
            <div className="selected-filter-border"> </div>{' '}
            {(() => {
              const filterToDisplay = filters.find((filter) => filter.key === selectedFilter);

              if (!filterToDisplay) return null;

              let valueToShow;
              if (filterToDisplay.type === filterType.MULTISELECT) {
                valueToShow = (
                  <div className="multi-select-field-wrap">
                    <MultiSelectDropdown
                      name={filterToDisplay.key}
                      placeholderFilled={t(filterToDisplay.name)}
                      title={t(filterToDisplay.name)}
                      options={
                        filterToDisplay?.listOfValue?.map((item) => ({
                          ...item,
                          label: t(item.label),
                        })) || []
                      }
                      placeholder={t(filterToDisplay.name)}
                      search
                      version="v2"
                      selectedValues={
                        Array.isArray(filterToDisplay.value) ? filterToDisplay.value : []
                      }
                      onSelectionChange={handleMultiSelectField}
                      fontSize="small"
                    />
                  </div>
                );
              }
              return valueToShow;
            })()}
          </div>
        )}
      </div>
      <div className="form-buttons-wrap">
        <MyButton label={t('cancel')} buttonType="error" onClickFunc={handleClearFilters} />
        <MyButton
          label={t('apply')}
          buttonType="submit"
          onClickFunc={isAnyFilterSelected ? handleApplyFilter : undefined}
          loading={filterListLoading}
          disableButton={filterListLoading || !isAnyFilterSelected}
        />
      </div>
    </div>
  );
}

export default CustomTableFilterV2;
